import React, { useState } from "react";
import GuideLayout from "../../components/layout/GuideLayout";
import Section from "../../components/layout/Section";
import {
  Box,
  IconButton,
  Stack,
  Collapse,
  Link,
  Typography,
} from "@mui/material";
import RatesSimulator from "../../components/RatesSimulator.js";
import GambaRates from "../../data/GambaRates.js";
import CustomPoolBuilder from "../../components/CustomPoolBuilder";
import { ExpandLess, ExpandMore } from "@mui/icons-material";

const sections = [
  { id: "intro", title: "Top" },
  { id: "mc", title: "Regular Mystery Capsule (10 items)" },
  { id: "pet-cap", title: "Pet Day Mystery Capsule" },
  { id: "mini-cap-1", title: "Mini Mystery Capsule #1 (6 items)" },
  { id: "mini-cap-2", title: "Mini Mystery Capsule #2 (5 items)" },
  { id: "closet-essentials-cap", title: "Closet Essentials Mystery Capsule" },
  { id: "retired-cap-no-bonus", title: "Retired Mystery Capsule (No Bonus)" },
  { id: "retired-cap-with-bonus", title: "Retired Mystery Capsule (With Bonus)" },
  { id: "birthday-cap", title: "Birthday Mystery Capsule" },
  { id: "new-years-cap", title: "New Years Mystery Capsule" },
  { id: "gbc", title: "Gift Box Mystery Capsule" },
  { id: "bfgbc", title: "Black Friday Gift Box Mystery Capsule" },
  { id: "gram", title: "Grams" },
  { id: "bday-candles", title: "Birthday Candles" },
];

const DropRateSimulatorPage = () => {

  const [showCustomPool, setShowCustomPool] = useState(true);

  const handleToggle = () => setShowCustomPool((prev) => !prev);

  return (
    <GuideLayout sections={sections}>
      <Section id="intro" title="Drop Rate Simulator">
        <Typography
          variant="caption"
          color="secondary"
          sx={{ mt: -1, mb: 2, display: "block" }}
        >
          Last updated: October 2026
        </Typography>
        <Typography paragraph>
          Want to try your luck but not with actual NC? Try our simulator! All these probabilities are pulled from the <Link href="https://classic.support.neopets.com/hc/en-us/articles/37898249845261-NC-Mall-Drop-Rates">official drop rates from the faq</Link> on site.
        </Typography>
        <Typography paragraph>
          Note: All this is for fun and this is just a simulator. The results will not necessarily be what you get in game. Your actual results may vary - that's how probabilities work! Please do not make any financial decisions just from this. The rates are also subject to change anytime by TNT, so check carefully before making any purchases!
        </Typography>
      </Section>

      <Section id="custom">
        <Box
          sx={{
            mb: 6,
            p: 3,
            borderRadius: 2,
            boxShadow: 0,
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(100, 181, 246, 0.1)" // soft blue tint in dark mode
                : "rgba(100, 181, 246, 0.15)", // soft blue in light mode
            border: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="h4" fontWeight="bold" sx={theme => ({
              color: theme.palette.accent.main
            })}>
              Customize Your Own Simulator
            </Typography>
            <IconButton onClick={handleToggle} size="small">
              {showCustomPool ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Stack>

          <Typography paragraph>
            Don't like the example images? You can customize your own here - just grab the image urls and item names and press create!
          </Typography>
          <Collapse in={showCustomPool}>
            <CustomPoolBuilder />
          </Collapse>
        </Box>
      </Section>

      <Section id="dye">
        <RatesSimulator
          description={"Please note that there is no official published rates for this capsule. It does not have LEs so this simulator assumes the official retired mystery capsule rate where you have a 88% chance to get 1 item and 12% chance for 2 items. There are 30 items in this single capsule."}
          boxName={GambaRates.shitCap.name}
          boxImage={GambaRates.shitCap.image}
          outcomes={GambaRates.shitCap.outcomes}
          pools={GambaRates.shitCap.pools}
        />
      </Section>

      <Section id="mc">
        <RatesSimulator
          description={"Using Backgrounds Mystery Capsule as an example."}
          boxName={GambaRates.mysteryCapsule.name}
          boxImage={GambaRates.mysteryCapsule.image}
          outcomes={GambaRates.mysteryCapsule.outcomes}
          pools={GambaRates.mysteryCapsule.pools}
        />
      </Section>

      <Section id="pet-cap">
        <RatesSimulator
          description={"Rates also apply for Mini Mystery Capsules that include an LE item. Using Poogle Day Y27 Mini Mystery Capsule as an example."}
          boxName={GambaRates.petDay.name}
          boxImage={GambaRates.petDay.image}
          outcomes={GambaRates.petDay.outcomes}
          pools={GambaRates.petDay.pools}
        />
      </Section>

      <Section id="mini-cap-1">
        <RatesSimulator
          description={"Using Special Effects Mini Mystery Capsule as an example."}
          boxName={GambaRates.mini1.name}
          boxImage={GambaRates.mini1.image}
          outcomes={GambaRates.mini1.outcomes}
          pools={GambaRates.mini1.pools}
        />
      </Section>

      <Section id="mini-cap-2">
        <RatesSimulator
          description={"Using So Mini Baby Colours Mystery Capsule as an example."}
          boxName={GambaRates.mini2.name}
          boxImage={GambaRates.mini2.image}
          outcomes={GambaRates.mini2.outcomes}
          pools={GambaRates.mini2.pools}
        />
      </Section>

      <Section id="closet-essentials-cap">
        <RatesSimulator
          description={"Using Closet Essentials II Mystery Capsule as an example. The closet essentials caps have a large pool and categories to choose from, so we've simplified results to show whether you got a cap that would grant you 2 items or just 1 item."}
          boxName={GambaRates.closetEssentials.name}
          boxImage={GambaRates.closetEssentials.image}
          outcomes={GambaRates.closetEssentials.outcomes}
          pools={GambaRates.closetEssentials.pools}
        />
      </Section>

      <Section id="retired-cap-no-bonus">
        <RatesSimulator
          description={"The caps for retired items have a large themed pool and categories to choose from, so we've simplified results to show whether you got a cap that would grant you 2 items or just 1 item."}
          boxName={GambaRates.retiredNoBonus.name}
          boxImage={GambaRates.retiredNoBonus.image}
          outcomes={GambaRates.retiredNoBonus.outcomes}
          pools={GambaRates.retiredNoBonus.pools}
        />
      </Section>

      <Section id="retired-cap-with-bonus">
        <RatesSimulator
          description={"The caps for retired items have a large themed pool and categories to choose from, so we've simplified results to show whether you got a cap that would grant you 2 items + LE or just 1 item."}
          boxName={GambaRates.retiredWithBonus.name}
          boxImage={GambaRates.retiredWithBonus.image}
          outcomes={GambaRates.retiredWithBonus.outcomes}
          pools={GambaRates.retiredWithBonus.pools}
        />
      </Section>

      <Section id="birthday-cap">
        <RatesSimulator
          description={"Using NC Mall 17th Void Birthday Mystery Capsule as an example. The birthday caps have a large pool of items, so we've simplified results to show whether you got a cap that would grant you 2 items + LE or just 1 item."} 
          boxName={GambaRates.birthday.name}
          boxImage={GambaRates.birthday.image}
          outcomes={GambaRates.birthday.outcomes}
          pools={GambaRates.birthday.pools}
        />
      </Section>

      <Section id="new-years-cap">
        <RatesSimulator
          description={"Using New Year 2023 Celebration Mystery Capsule as an example."} 
          boxName={GambaRates.newYears.name}
          boxImage={GambaRates.newYears.image}
          outcomes={GambaRates.newYears.outcomes}
          pools={GambaRates.newYears.pools}
        />
      </Section>

      <Section id="gbc">
        <RatesSimulator
          description={"Using Smorgasbord Gift Box Mystery Capsule as an example."} 
          boxName={GambaRates.giftBox.name}
          boxImage={GambaRates.giftBox.image}
          outcomes={GambaRates.giftBox.outcomes}
          pools={GambaRates.giftBox.pools}
        />
      </Section>

      <Section id="bfgbc">
        <RatesSimulator
          description={"Using 25th Anniversary Aisha Black Friday Gift Box Mystery Capsule as an example."} 
          boxName={GambaRates.blackFriday.name}
          boxImage={GambaRates.blackFriday.image}
          outcomes={GambaRates.blackFriday.outcomes}
          pools={GambaRates.blackFriday.pools}
        />
      </Section>

      <Section id="gram">
        <RatesSimulator
          description={"Using Altador Cup Naturally Fanrific Gram as an example."} 
          boxName={GambaRates.gram.name}
          boxImage={GambaRates.gram.image}
          outcomes={GambaRates.gram.outcomes}
          pools={GambaRates.gram.pools}
        />
      </Section>

      <Section id="bday-candles">
        <RatesSimulator
          description={"Using 14th Birthday Fairgrounds Wish Candle as an example."} 
          boxName={GambaRates.candles.name}
          boxImage={GambaRates.candles.image}
          outcomes={GambaRates.candles.outcomes}
          pools={GambaRates.candles.pools}
        />
      </Section>

    </GuideLayout>
  );
};

export default DropRateSimulatorPage;
